/* eslint-disable boundaries/element-types */
import { DownCircleOutlined } from '@ant-design/icons'
import * as Collapsible from '@radix-ui/react-collapsible'
import { Pencil2Icon, PlusCircledIcon, TrashIcon } from '@radix-ui/react-icons'
import { Popover, Table } from '@radix-ui/themes'
import { Button } from 'antd'
import { convertToUnit } from 'components/form/formHelpers'
import {
  generateTableCellsFromFeatureLimitedFields,
  getFormFieldsForFeatureType,
  getNewJSONForFeatureType,
  getSortedFeatureList,
} from 'features/assetDatabase'
import { useGeckoLocale } from 'features/locale'
import parse from 'html-react-parser'
import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import styled from 'styled-components'
import {
  GeckoEditableFeature,
  GeckoFeatureModelType,
  GeckoFeaturesJSON,
  getFormFieldByDataKey,
  SortTableByField,
} from 'types'
import { v4 as uuid4 } from 'uuid'

export const StyledCollapsibleRoot = styled(Collapsible.Root)`
  margin: 10px 0;
`

export const StyledCollapsibleHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  background-color: ${({ theme }) => theme.forecast.background4};
  border-radius: 5px 5px 0 0;
`

export const StyledHeaderTitle = styled.div`
  padding-left: 10px;
  color: ${({ theme }) => theme.forecast.text1};
  font-weight: bold;
`

export const StyledCollapsibleTrigger = styled(Collapsible.Trigger)`
  background-color: transparent;
  border: none;
  cursor: pointer;
`

interface CustomTableRootProps {
  className?: string
  children?: ReactNode
}

const CustomTableRoot: React.FC<CustomTableRootProps> = ({
  className,
  children,
  ...props
}: CustomTableRootProps) => (
  <Table.Root className={className} {...props}>
    {children}
  </Table.Root>
)

export const StyledTableRoot = styled(CustomTableRoot)`
  background-color: ${({ theme }) => theme.forecast.background3};
`

export const StyledTableMessage = styled.div`
  background-color: ${({ theme }) => theme.forecast.background3};
  width: 100%;
  border-bottom: solid 1px ${({ theme }) => theme.forecast.background4};
  padding: 5px;
  cursor: default;

  a {
    color: ${({ theme }) => theme.forecast.text1};
  }
`

export const StyledColumnHeaderCell = styled(Table.ColumnHeaderCell)`
  padding-left: 10px;
  color: ${({ theme }) => theme.forecast.text1};
  border-bottom: solid 1px ${({ theme }) => theme.forecast.background4};
  height: 35px;
  align-content: center;
`

const StyledButtonColumnHeaderCell = styled(Table.ColumnHeaderCell)`
  color: ${({ theme }) => theme.forecast.text1};
  border-bottom: solid 1px ${({ theme }) => theme.forecast.background4};
  height: 35px;
  align-content: center;
  display: flex;
  width: 100%;
  justify-content: end;
  padding-right: 16px;
`

export const StyledCell = styled(Table.Cell)`
  padding-left: 10px;
  color: ${({ theme }) => theme.forecast.text1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledButtonCell = styled(Table.Cell)`
  padding-left: 5px;
  color: ${({ theme }) => theme.forecast.text1};
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  justify-content: end;
  min-width: 80px;
  display: flex;
`

export const StyledButton = styled(Button)`
  background-color: transparent;
  width: 32px;
  margin-right: 5px;

  & > svg {
    min-width: 16px;
  }
`

export const StyledTrashButton = styled(StyledButton)`
  &:not(:disabled):hover {
    background-color: red !important;
  }
`

const StyledPopoverContent = styled(Popover.Content)`
  border: 2px solid ${({ theme }) => theme.forecast.background4};
  background-color: ${({ theme }) => theme.forecast.background3};
  color: ${({ theme }) => theme.forecast.text1};
  padding: 0;
  border-radius: 0.25rem;
  max-width: 300px;
  z-index: 1000;
`

const StyledComponentEditButton = styled(Button)`
  background-color: ${({ theme }) => theme.forecast.background4};
`

const parseToReactElement = (input: string): React.ReactNode => {
  // Try parsing the HTML string into React components
  try {
    const reactElement = parse(input.trim())
    return reactElement
  } catch (error) {
    console.error('Error parsing HTML string:', error)
  }
}

interface EditFeatureTableInterface {
  tableTitle: string
  featureJSONMap: Record<string, GeckoFeaturesJSON>
  fieldsToShow: string[]
  featureClass: GeckoEditableFeature
  featureType: GeckoFeatureModelType
  sortBy?: SortTableByField[]
  selectFeature: Dispatch<SetStateAction<SelectedFeature | undefined>>
  deleteFeature: (featureID: string) => void
  disableEditing?: boolean
  tableMessage?: string
}

// The required props to load the EditableFeatureForm
// If FeatureID and FeatureJSON are defined, we use instantiate an instance using the FeatureClass
// If FeatureID and FeatureJSON are not defined, we pass the FeatureClass directly
export interface SelectedFeature {
  featureID: string | undefined
  featureJSON: GeckoFeaturesJSON | undefined
  featureType: GeckoFeatureModelType
}

/**
 * This component is building the Feature Tables displayed while editing the rich asset model. Functionally, this
 * component allows us to visualize a list of the features (separated by type), select individual ones to edit, and
 * also add new ones.
 * @param tableTitle          The title to be displayed on the collapsable panel
 * @param featureJSONMap      An object of a single feature type, where the key is the feature ID and the value is the JSON defining that feature
 * @param fieldsToShow        A list of strings representing the FormFields key for that feature
 * @param featureClass        A GeckoEditableFeature child class that can be instantiated using the JSONs provided
 * @param sortBy              A list of SortTableByField objects, to sort the features by in the table
 * @param selectFeature       A callback function to return the selected feature information to the parent container (used for Adding & Editing)
 * @param deleteFeature       A callback function to delete the selected feature form the Asset Model
 * @param disableEditing      Boolean that will disable the buttons for adding or editing the listed features
 * @param tableMessage        An optional string to show at the top of a table to describe that feature if necessary
 * @constructor
 */
export const EditFeatureTable = ({
  tableTitle,
  featureJSONMap,
  fieldsToShow,
  featureClass,
  featureType,
  sortBy,
  selectFeature,
  deleteFeature,
  disableEditing,
  tableMessage,
}: EditFeatureTableInterface) => {
  const { measurementSystem } = useGeckoLocale()

  // Using the fields to show as keys, look at the available form fields and parse out the labels to be used as headers
  const tableColumnHeaders = fieldsToShow.map((field) => {
    const availableFormFields = getFormFieldsForFeatureType(
      getNewJSONForFeatureType(featureType)
    )
    const formField = getFormFieldByDataKey(availableFormFields, field)
    if (!formField) {
      return
    }

    const label = formField.label
    const displayUnit = convertToUnit(
      measurementSystem,
      formField.unit_magnitude
    )
    const header = `${label}${displayUnit ? ` (${displayUnit.name})` : ''}`
    return <StyledColumnHeaderCell key={label}>{header}</StyledColumnHeaderCell>
  })

  // Sort the list of items, allowing for an arbitrary number of sorting priorities
  const sortedFeatureList = getSortedFeatureList(featureJSONMap, sortBy)

  return (
    <StyledCollapsibleRoot key={uuid4().slice(0, 6)}>
      <StyledCollapsibleHeader>
        <StyledHeaderTitle>{tableTitle}</StyledHeaderTitle>
        <StyledCollapsibleTrigger>
          <DownCircleOutlined style={{ color: 'white' }} />
        </StyledCollapsibleTrigger>
      </StyledCollapsibleHeader>
      <Collapsible.Content>
        {tableMessage ? (
          <StyledTableMessage>
            <span>{parseToReactElement(tableMessage)}</span>
          </StyledTableMessage>
        ) : undefined}
        <StyledTableRoot>
          <Table.Header>
            <Table.Row>
              {tableColumnHeaders}
              <StyledButtonColumnHeaderCell>
                <StyledButton disabled={disableEditing}>
                  <PlusCircledIcon
                    data-testid={featureClass.id}
                    onClick={() => {
                      selectFeature({
                        featureID: undefined,
                        featureJSON: undefined,
                        featureType,
                      })
                    }}
                  />
                </StyledButton>
              </StyledButtonColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedFeatureList.map(
              ([featureID, featureJSON]: [string, GeckoFeaturesJSON]) => {
                return (
                  <Table.Row key={featureID}>
                    {generateTableCellsFromFeatureLimitedFields(
                      [featureID, featureJSON],
                      measurementSystem,
                      convertToUnit,
                      fieldsToShow
                    ).map((cell) => (
                      <StyledCell key={cell.fieldName}>{cell.value}</StyledCell>
                    ))}
                    <StyledButtonCell>
                      <StyledButton disabled={disableEditing}>
                        <Pencil2Icon
                          onClick={() => {
                            selectFeature({
                              featureID: featureID,
                              featureJSON: featureJSON,
                              featureType: featureType,
                            })
                          }}
                        />
                      </StyledButton>
                      <Popover.Root>
                        <Popover.Trigger>
                          <StyledTrashButton disabled={disableEditing}>
                            <TrashIcon />
                          </StyledTrashButton>
                        </Popover.Trigger>
                        <StyledPopoverContent>
                          <div>
                            <div style={{ textAlign: 'center' }}>
                              Are you sure you want to delete this feature from
                              the component? This cannot be undone.
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'center',
                                paddingTop: '10px',
                                gap: '10px',
                              }}>
                              <StyledComponentEditButton
                                onClick={() => {
                                  deleteFeature(featureID)
                                }}>
                                Confirm
                              </StyledComponentEditButton>
                            </div>
                          </div>
                        </StyledPopoverContent>
                      </Popover.Root>
                    </StyledButtonCell>
                  </Table.Row>
                )
              }
            )}
          </Table.Body>
        </StyledTableRoot>
      </Collapsible.Content>
    </StyledCollapsibleRoot>
  )
}
